import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import { useAuth } from '../auth/AuthContext';
import { API_URL } from '../config/api';
import Wordmark from '../components/Wordmark';
import {
  GOOGLE_MESSAGE_SOURCE,
  buildGoogleAuthUrl,
  hasGoogleCallbackParams,
  openGoogleChannel,
  openGooglePopup,
  parseGoogleCallbackParams,
  rememberReturnSearch,
  verifyGoogleCallback,
} from '../services/GoogleLogin';
import './Login.scss';

// Sign in with Apple (web) Service ID — the .p8 private key lives on the API
// (config.apple), never here. Apple does a strict, exact match on redirectURI
// against the Service ID's registered Return URLs, which carry a trailing
// slash, so we append one to window.location.origin. localhost is not an
// allowed Apple Return URL, so Apple login only works on the deployed domains
// (and only once this portal's origin is added to the Service ID).
const APPLE_SERVICE_ID = 'app.outfino.admin.sid';

export default function Login() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { user, loading, login, loginWithToken } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // A Google sign-in popup is open and we're waiting for it to come back.
  const [googleBusy, setGoogleBusy] = useState(false);
  const googlePopup = useRef(null);
  // A result can reach us twice (broadcast + opener postMessage). The first
  // one consumes the one-time state/nonce, so the second would "fail" — this
  // latch drops it instead.
  const googleHandled = useRef(false);

  // Already logged in → straight to the dashboard.
  useEffect(() => {
    if (!loading && user) navigate('/dashboard', { replace: true });
  }, [loading, user, navigate]);

  async function finishSocial(res, data, failMsg) {
    if (!res.ok) {
      setErr(failMsg);
      return;
    }
    const u = data.message?.user ?? data.user;
    const token = data.message?.token ?? data.token;
    if (!token) {
      setErr(failMsg);
      return;
    }
    await loginWithToken(token, u);
    navigate('/dashboard', { replace: true });
  }

  async function handleGoogleCredential(idToken) {
    setErr('');
    try {
      const res = await fetch(`${API_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }),
      });
      const data = await res.json();
      await finishSocial(res, data, t('login.googleFailed'));
    } catch {
      setErr(t('login.networkError'));
    }
  }

  // Opens Google's consent screen. See services/GoogleLogin.js for why this is
  // a hand-rolled popup instead of Google's own rendered button.
  function handleGoogleLogin() {
    setErr('');
    googleHandled.current = false;
    const url = buildGoogleAuthUrl();
    const popup = openGooglePopup(url);
    if (!popup) {
      // Popup blocked. Run the very same flow in this tab — /google-callback
      // bounces back here with the fragment, and the mount effect below
      // finishes the sign-in. Stash the query string first so leaving the page
      // doesn't drop it.
      rememberReturnSearch(window.location.search);
      window.location.assign(url);
      return;
    }
    googlePopup.current = popup;
    setGoogleBusy(true);
  }

  // Shared tail for both routes a Google response can arrive by: relayed from
  // the popup, or found in our own URL after the popup-blocked fallback.
  function handleGoogleResult(params) {
    if (googleHandled.current) return;
    googleHandled.current = true;
    setGoogleBusy(false);
    if (params?.error) {
      // access_denied means the user closed the consent screen. That's a
      // cancel, not a failure — saying nothing is the right response.
      if (params.error !== 'access_denied') setErr(t('login.googleFailed'));
      return;
    }
    const idToken = verifyGoogleCallback(params);
    if (!idToken) {
      setErr(t('login.googleFailed'));
      return;
    }
    handleGoogleCredential(idToken);
  }

  async function handleAppleLogin() {
    setErr('');
    if (!window.AppleID) {
      setErr(t('login.appleUnavailable'));
      return;
    }
    try {
      const resp = await window.AppleID.auth.signIn();
      const { id_token: identityToken, code: authorizationCode } =
        resp.authorization || {};
      // Apple only sends the name on the very first authorization.
      const givenName = resp.user?.name?.firstName;
      const familyName = resp.user?.name?.lastName;
      const res = await fetch(`${API_URL}/auth/apple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identityToken,
          authorizationCode,
          givenName,
          familyName,
        }),
      });
      const data = await res.json();
      await finishSocial(res, data, t('login.appleFailed'));
    } catch (e) {
      // signIn() rejects (popup_closed_by_user etc.) → treat as a no-op cancel
      // rather than a hard error.
      if (
        e?.error === 'popup_closed_by_user' ||
        e?.error === 'user_cancelled_authorize'
      )
        return;
      setErr(t('login.appleFailed'));
    }
  }

  // Listen for the Google result the callback page relays. Mounted once, for
  // the whole life of the login screen: the popup can come back at any point
  // after the click, including after a re-render.
  useEffect(() => {
    const channel = openGoogleChannel();

    function ack() {
      // Tells the callback page it was heard, so it closes instead of
      // bouncing to the login screen inside its own popup.
      channel?.postMessage({ source: GOOGLE_MESSAGE_SOURCE, ack: true });
    }

    function accept(data, replyTo, replyOrigin) {
      if (data?.source !== GOOGLE_MESSAGE_SOURCE || data?.ack) return;
      ack();
      if (replyTo)
        replyTo.postMessage(
          { source: GOOGLE_MESSAGE_SOURCE, ack: true },
          replyOrigin
        );
      googlePopup.current?.close?.();
      googlePopup.current = null;
      handleGoogleResult(data);
    }

    function onWindowMessage(event) {
      if (event.origin !== window.location.origin) return;
      accept(event.data, event.source, event.origin);
    }

    if (channel) channel.onmessage = (event) => accept(event.data);
    window.addEventListener('message', onWindowMessage);
    return () => {
      window.removeEventListener('message', onWindowMessage);
      channel?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The popup can also be dismissed without answering. Nothing arrives in that
  // case, so watch for it closing and drop the pending state.
  useEffect(() => {
    if (!googleBusy) return undefined;
    const timer = setInterval(() => {
      if (googlePopup.current?.closed) {
        googlePopup.current = null;
        setGoogleBusy(false);
      }
    }, 500);
    return () => clearInterval(timer);
  }, [googleBusy]);

  // Popup-blocked fallback: the flow ran in this tab, so Google's answer is
  // sitting in our own URL. Strip it before doing anything else — a fragment
  // left in the address bar would be replayed by a reload.
  useEffect(() => {
    if (!hasGoogleCallbackParams()) return;
    const params = parseGoogleCallbackParams();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search
    );
    handleGoogleResult(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load + init Apple Sign-In.
  useEffect(() => {
    const scriptId = 'apple-auth-script';
    function initApple() {
      if (!window.AppleID) return;
      window.AppleID.auth.init({
        clientId: APPLE_SERVICE_ID,
        scope: 'name email',
        redirectURI: `${window.location.origin}/`,
        usePopup: true,
      });
    }
    if (document.getElementById(scriptId)) {
      initApple();
      return;
    }
    const script = document.createElement('script');
    script.id = scriptId;
    script.src =
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.defer = true;
    script.onload = initApple;
    document.head.appendChild(script);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch (ex) {
      setErr(
        ex.status === 401 || ex.status === 400
          ? t('login.wrongCredentials')
          : t('login.networkError')
      );
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-page__top">
        <Link to="/" className="login-page__back">
          <ArrowLeft size={16} />
          {t('login.back')}
        </Link>
      </div>

      <div className="login-page__center">
        <div className="login-card">
          <div className="login-card__brand">
            <Wordmark size={38} />
          </div>
          <h1 className="login-card__title">{t('login.title')}</h1>
          <p className="login-card__subtitle">{t('login.subtitle')}</p>

          {/* Social sign-in */}
          <div className="social-auth">
            <button
              type="button"
              className="social-btn"
              onClick={handleGoogleLogin}
              disabled={googleBusy}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  fill="#4285F4"
                  d="M23.745 12.27c0-.79-.07-1.54-.19-2.27h-11.3v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
                />
                <path
                  fill="#34A853"
                  d="M12.255 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96h-3.98v3.09C3.515 21.3 7.565 24 12.255 24z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.525 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62h-3.98a11.86 11.86 0 000 10.76l3.98-3.09z"
                />
                <path
                  fill="#EA4335"
                  d="M12.255 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C18.205 1.19 15.495 0 12.255 0c-4.69 0-8.74 2.7-10.71 6.62l3.98 3.09c.95-2.85 3.6-4.96 6.73-4.96z"
                />
              </svg>
              {t('login.google')}
            </button>
            <button
              type="button"
              className="social-btn apple-btn"
              onClick={handleAppleLogin}
            >
              <svg width="17" height="17" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
              </svg>
              {t('login.apple')}
            </button>
          </div>

          <div className="login-divider">
            <span>{t('login.or')}</span>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            <label>
              <span>{t('login.email')}</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </label>
            <label>
              <span>{t('login.password')}</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
            </label>

            {err && <div className="login-form__error">{err}</div>}

            <button
              type="submit"
              className="btn-o btn-o--dark login-form__submit"
              disabled={submitting}
            >
              {submitting ? t('login.loading') : t('login.submit')}
            </button>
          </form>

          <div className="login-card__notice">{t('login.notice')}</div>
        </div>
      </div>
    </div>
  );
}
